/**
 * The desktop launcher home is not a hub surface (card t_f0ea3681).
 *
 * The owner's report, on the Windows desktop app: *"main home page plays and
 * shows notification messges from last connection!! the home page should not
 * make any conneciton when on windowss deskotp. (electron).."*
 *
 * It did, and the measurement says why. The launcher tab boots from the stored
 * session and `authStore.checkAuth()` re-opens the hub socket as soon as the
 * token verifies (`[AuthStore] Socket reconnected on checkAuth`), the hub answers
 * with its own `[SocketService] Connection details`, and `NotificationToast` —
 * mounted on `/home` on purpose — announces whatever the hub pushes: balloon,
 * alert tone and speech. Measured on the dev app parked on `/home`, 2026-09-30:
 * `socket.connected: true, url: ws://127.0.0.1:16161`, and the hub's log named
 * the very socket the parked page held.
 *
 * The rule this module writes down, and the one decision behind it:
 *
 *   - **the desktop launcher home holds no hub connection at all.** `/home` in
 *     Electron is the picker — the server cards — and the launcher never becomes
 *     a hub session (`components/server/ServersSection.vue`: every connect entry
 *     point opens a *tab*). A surface that opens no hub socket can receive
 *     nothing, announce nothing, and speak nothing.
 *   - What the launcher legitimately holds **stays**: the stored session token,
 *     the account's cloud list and the LAN availability probe. None of those is
 *     a hub connection, and the probe is what paints each card's reachability.
 *   - **Only the desktop.** The owner named the desktop app; a browser tab and a
 *     Capacitor WebView keep today's behaviour exactly (there `/home` *is* the
 *     window a hub session lands in).
 *   - **An unknown surface is never muted.** Until the app root has reported the
 *     route it is on, `onDesktopLauncherHome()` answers `false` — the same
 *     principle as the notice layer's "a desktop surface whose shell has not
 *     answered announces": a rule that mutes on missing information silences the
 *     app, which is worse than the defect it fixes.
 *
 * Pure where it matters: `isLauncherSurface()` and `mountsNotificationChrome()`
 * take their facts as arguments and are asserted in `launcherSurface.test.ts`
 * without pinia, a socket or a DOM. `../config/runtime` is imported for
 * `isElectron()` alone (it reads `window.electron`, and answers `false` with no
 * window at all — which is what a Node test sees).
 */
import { isElectron } from '../config/runtime';

/** The one route the desktop app's launcher home is on. */
export const LAUNCHER_HOME_PATH = '/home';

export interface LauncherSurfaceFacts {
  /**
   * The desktop app — `window.electron` is present. Not the browser, not the
   * Capacitor WebView (this is `config/runtime.ts` → `isElectron()`).
   */
  desktop: boolean;
  /** The route THIS window is on (`route.path`). */
  path: string;
}

/**
 * Whether this window is the desktop launcher home. `/home` is the launcher on
 * every platform; only in Electron does that make it a *picker* rather than the
 * window a hub session opens into — so only there is it not a hub surface.
 */
export function isLauncherSurface(facts: LauncherSurfaceFacts): boolean {
  return facts.desktop && facts.path === LAUNCHER_HOME_PATH;
}

/**
 * Whether the notification chrome — the balloon host, which is also the thing
 * that plays the alert tone and speaks — may mount on this surface.
 *
 * The rule it states (`App.vue`'s `showNotificationChrome`): every non-bare
 * layout carries app chrome and needs the toast, and the Servers page (`/home`)
 * is the historical exception that kept it there by name — except on the desktop
 * launcher, which must never paint, play or speak a notice.
 */
export function mountsNotificationChrome(facts: LauncherSurfaceFacts & { bare: boolean }): boolean {
  if (isLauncherSurface(facts)) return false;
  return !facts.bare || facts.path === LAUNCHER_HOME_PATH;
}

// ── the live surface ─────────────────────────────────────────────────────────

/**
 * The route the app root is currently on. Reported once from `App.vue` (a
 * watcher on `route.path`, immediate) — the app root is what owns a route, and
 * this module stays a value, not a router.
 */
let surfacePath = '';

/** The app root reports the route this window is on. */
export function setSurfacePath(path: string): void {
  surfacePath = path;
}

/** The route as last reported (empty until the app root has spoken). */
export function currentSurfacePath(): string {
  return surfacePath;
}

/**
 * The live answer, asked where it is spent (`stores/socket.ts` before it opens
 * a socket, `services/noticeSurface.ts` before a notice announces, `App.vue`
 * before it reconnects a socket the visibility handler thinks is missing).
 */
export function onDesktopLauncherHome(): boolean {
  return isLauncherSurface({ desktop: isElectron(), path: surfacePath });
}
